import { useState, useCallback } from 'react';
import { ImageIcon, Pencil, Upload, FileText, Receipt, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ProjectHeaderProps {
  project: {
    id: string;
    name: string;
    weddingDate: string | null;
    venueName: string | null;
    guestCount: number | null;
    photoUrl: string | null;
  };
  onEditDetails: () => void;
  onUploadQuotes: () => void;
  onUploadContracts: () => void;
  onPhotoUploaded: () => void;
}

function formatWeddingDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr.slice(0, 10) + 'T00:00:00');
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function ProjectHeader({ project, onEditDetails, onUploadQuotes, onUploadContracts, onPhotoUploaded }: ProjectHeaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const resolvedPhotoUrl = project.photoUrl ? `/api/files/${project.photoUrl}` : null;

  const uploadFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setIsUploading(true);
    const form = new FormData();
    form.append('photo', file);
    await fetch(`/api/projects/${project.id}/photo`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    setIsUploading(false);
    onPhotoUploaded();
  }, [project.id, onPhotoUploaded]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 shadow-ambient">
      {/* Photo thumbnail */}
      <label className="flex size-16 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-border bg-muted">
        {resolvedPhotoUrl ? (
          <img src={resolvedPhotoUrl} alt="Project photo" className="size-full object-cover" />
        ) : (
          <ImageIcon className="size-6 text-muted-foreground/40" />
        )}
        <input type="file" accept="image/*" className="sr-only" onChange={handleFileInput} disabled={isUploading} />
      </label>

      {/* Project info */}
      <div className="flex flex-1 items-center gap-3 overflow-hidden text-sm">
        <span className="shrink-0 text-base font-semibold">{project.name}</span>
        {project.weddingDate && (
          <>
            <span className="text-border">|</span>
            <span className="shrink-0 text-muted-foreground">{formatWeddingDate(project.weddingDate)}</span>
          </>
        )}
        {project.venueName && (
          <>
            <span className="text-border">|</span>
            <span className="shrink-0 text-muted-foreground">{project.venueName}</span>
          </>
        )}
        {project.guestCount && (
          <>
            <span className="text-border">|</span>
            <span className="shrink-0 text-muted-foreground">{project.guestCount.toLocaleString()} guests</span>
          </>
        )}
      </div>

      {/* Actions */}
      <div className="flex shrink-0 gap-2">
        <Button variant="outline" size="sm" onClick={onEditDetails}>
          <Pencil className="mr-1.5 size-3.5" />
          Edit Details
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Upload className="mr-1.5 size-3.5" />
              Upload Documents
              <ChevronDown className="ml-1 size-3.5 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onUploadQuotes}>
              <Receipt className="mr-2 size-4" />
              Quotes
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onUploadContracts}>
              <FileText className="mr-2 size-4" />
              Contracts
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
